import { FC, memo } from 'react';
import { Box, CircularProgress, IconButton, TextField, useTheme } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SelectWithLabel } from '@components';
import {
  formFieldContainerSx,
  formSelectFieldStyles,
  formTextFieldStyles,
} from '../styles/formFieldStyles';
import { SaveStatus, UserFormRow, UserRole } from '../types/userDetailsForm';
import { RowFormErrors, RowTouchedFields } from '../hooks/useUserDetailsForm';

type RoleOption = { value: UserRole; label: string };

const DEFAULT_ROLE_OPTIONS: RoleOption[] = [{ value: 'VIEWER', label: 'Viewer' }];

interface UserDetailsFormProps {
  rows: UserFormRow[];
  saveStatuses: SaveStatus[];
  isSaving: boolean;
  hasSaveAttempted: boolean;
  rowTouchedFields: RowTouchedFields;
  rowFormErrors: RowFormErrors;
  isEditMode?: boolean;
  getRoleOptions?: (email: string) => RoleOption[];
  onFieldChange: (index: number, field: keyof UserFormRow, value: string) => void;
  onFieldBlur: (index: number, field: keyof UserFormRow) => void;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
  onRetry: (index: number, retryId: string) => void;
}

const UserDetailsForm: FC<UserDetailsFormProps> = ({
  rows,
  saveStatuses,
  isSaving,
  hasSaveAttempted,
  rowTouchedFields,
  rowFormErrors,
  isEditMode = false,
  getRoleOptions,
  onFieldChange,
  onFieldBlur,
  onAddRow,
  onRemoveRow,
  onRetry,
}) => {
  const theme = useTheme();

  const getRowStatus = (index: number) => saveStatuses.find((s) => s.index === index);

  const getFieldError = (index: number, field: keyof UserFormRow): string =>
    rowTouchedFields[index]?.[field] ? rowFormErrors[index]?.[field] || '' : '';

  const getRowActions = (index: number) => {
    if (isSaving || hasSaveAttempted) {
      const status = getRowStatus(index);

      if (!status) {
        return <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} />;
      }

      if (status.success) {
        return <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />;
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <CancelIcon sx={{ color: '#F44336', fontSize: 20 }} />
          <RefreshIcon
            sx={{ color: '#2F736E', fontSize: 20, cursor: isSaving ? 'default' : 'pointer' }}
            onClick={isSaving ? undefined : () => onRetry(index, status.retryId)}
          />
        </Box>
      );
    }

    if (isEditMode) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={() => onRemoveRow(index)}
          disabled={rows.length === 1}
          size="small"
          sx={{
            p: '2px',
            color: theme.palette.error.main,
            '&.Mui-disabled': { opacity: 0.4 },
            '&:hover': { background: 'none' },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
        {/* Reserve space for the add icon — only render it on the last row */}
        <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
          {index === rows.length - 1 && (
            <IconButton
              onClick={onAddRow}
              size="small"
              sx={{
                p: '2px',
                color: theme.palette.primary.main,
                '&:hover': { background: 'none' },
              }}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', mb: '2rem' }}>
      {rows.map((row, index) => {
        const roleOptions = getRoleOptions ? getRoleOptions(row.email) : DEFAULT_ROLE_OPTIONS;
        const isRowSaved = hasSaveAttempted && getRowStatus(index)?.success;
        const emailError = getFieldError(index, 'email');
        const usernameError = getFieldError(index, 'username');

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.875rem',
              alignItems: 'flex-start',
            }}
          >
            {/* Email */}
            <TextField
              label="Email *"
              placeholder="john.doe@pvxpartners.com"
              value={row.email}
              onChange={(e) => onFieldChange(index, 'email', e.target.value)}
              onBlur={() => onFieldBlur(index, 'email')}
              disabled={isEditMode || isSaving || Boolean(isRowSaved)}
              error={!!emailError}
              helperText={emailError}
              sx={{
                ...formTextFieldStyles,
                '& .MuiFormHelperText-root': { margin: 0, padding: '0.25rem 0' },
              }}
            />

            {/* Username */}
            <TextField
              label="User Name"
              placeholder="John Doe"
              value={row.username}
              onChange={(e) => onFieldChange(index, 'username', e.target.value)}
              onBlur={() => onFieldBlur(index, 'username')}
              disabled={isSaving || Boolean(isRowSaved)}
              error={!!usernameError}
              helperText={usernameError}
              sx={{
                ...formTextFieldStyles,
                '& .MuiFormHelperText-root': { margin: 0, padding: '0.25rem 0' },
              }}
            />

            {/* Role + fixed-width action slot in same cell — mirrors V1 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Box sx={formFieldContainerSx}>
                <SelectWithLabel
                  label="Role"
                  labelId={`role-select-${index}`}
                  menuItems={roleOptions}
                  value={row.userRole}
                  onChange={(value) => onFieldChange(index, 'userRole', value)}
                  isAddNew={false}
                  selectStyles={formSelectFieldStyles}
                  disabled={isEditMode || isSaving || Boolean(isRowSaved)}
                />
              </Box>

              <Box sx={{ width: 60, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {getRowActions(index)}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default memo(UserDetailsForm);
